import { FormattedMessage } from 'react-intl';
import { Text } from '@understory-io/leaf/text';

/**
 * The experience's per-booking maximum, as the ticket pickers apply it. A max
 * of 0 is "no rule", not "no seats" (see `deriveAvailability`).
 *
 * The pickers stop at the max and say why, so the per-booking limit is
 * explained where the guest sets the count — the time slots only describe the
 * event's own capacity (US-14091).
 */
export type PerBookingMax = {
  /** Upper bound for one variant's stepper. Never below its current quantity,
   *  so a basket already over the max can still step down. */
  variantMax: (variantQty: number) => number | undefined;
  /** The selection has reached the max, so every seat stepper's + is off. */
  reached: boolean;
  max: number | undefined;
};

export function perBookingMax(
  maxSeats: number | undefined,
  variants: readonly { id: string }[],
  quantities: Record<string, number>
): PerBookingMax {
  const max = maxSeats !== undefined && maxSeats > 0 ? maxSeats : undefined;
  // Add-ons don't take seats, so only the variants count towards the max.
  const total = variants.reduce((sum, v) => sum + (quantities[v.id] ?? 0), 0);
  return {
    max,
    reached: max !== undefined && total >= max,
    variantMax: (variantQty) =>
      max === undefined
        ? undefined
        : Math.max(variantQty, max - (total - variantQty)),
  };
}

/** Neutral hint shown once the selection reaches the per-booking max. Not an
 *  error — the guest did nothing wrong, the + button simply stopped. A status
 *  region, because Leaf's stepper only takes a description alongside an error
 *  tone, so this is how a screen reader learns why + stopped. */
export function PerBookingMaxHint({ id, max }: { id?: string; max: number }) {
  return (
    <Text id={id} role="status" style="body-small-regular" color="secondary">
      <FormattedMessage
        defaultMessage="Max. {count, plural, one {# ticket} other {# tickets}} per booking"
        description="Hint under the ticket picker once the selection reaches the experience's maximum tickets per booking"
        values={{ count: max }}
      />
    </Text>
  );
}
